import { ListViewLabel, ListViewRequiredLabel } from '@/components/ListView';
import { useTransactionItemDetailEdit } from '@/features/transaction_items/Detail/context/TransactionItemDetailEdit';
import { useSubmitUpdatePullDownTransactionItem } from '@/features/transaction_items/components/Form/hooks/useSubmitUpdatePullDownTransactionItem';
import { useTranslation } from '@/i18n';
import { maxLengthWithSurrogate } from '@/libs/validation/maxLengthWithSurrogate';
import {
  Bottom,
  ButtonV2,
  Form,
  FormCheckbox,
  FormInput,
  ListView,
  Top,
  useToastAPI,
} from '@moneyforward/ap-frontend-components';
import classNames from 'classnames';
import { FC, memo, useCallback, useMemo, useState } from 'react';
import {
  ErrorOption,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import styles from './Top.module.scss';

const cx = classNames.bind(styles);

export type EditFormProps = {
  label: string;
  visibility: boolean;
  tooltip_message?: string;
};

type TransactionItemDetailForm = {
  label: string;
  visibility: boolean;
  tooltip_message: string;
};

const leftRightRatio = {
  left: {
    max: '160px',
    min: '160px',
  },
  right: {},
};

export const EditForm: FC<EditFormProps> = memo(
  ({ label, visibility, tooltip_message }) => {
    const { t } = useTranslation();
    const notify = useToastAPI();
    const { onView } = useTransactionItemDetailEdit();
    const [isLoading, setIsLoading] = useState(false);

    const methods = useForm<TransactionItemDetailForm>({
      defaultValues: {
        label: label,
        visibility: visibility,
        tooltip_message: tooltip_message,
      },
      mode: 'onChange',
    });

    // Custom validation error handler to map between form types
    const handleValidationError = useCallback(
      (field: string, error: ErrorOption) => {
        if (field === 'label') {
          methods.setError('label', error);
        } else if (field === 'is_visible') {
          methods.setError('visibility', error);
        } else if (field === 'tooltip_message') {
          methods.setError('tooltip_message', error);
        }
      },
      [methods]
    );

    const { onSubmitUpdate } = useSubmitUpdatePullDownTransactionItem({
      setValidationError: handleValidationError,
      handlerSuccess: (successMsg: string) => {
        notify.success({ title: successMsg, duration: 5 });
        onView();
      },
      handlerError: (errorMsg: string) => {
        notify.error({ title: errorMsg, duration: 5 });
      },
      showLoading: setIsLoading,
    });

    const labelRules = useMemo(() => {
      const maxLengthValue = 20;
      return {
        required: t('transaction_items.validation_message.required'),
        ...maxLengthWithSurrogate(
          maxLengthValue,
          t('transaction_items.validation_message.maxLength', {
            maxLength: maxLengthValue,
          })
        ),
      };
    }, [t]);

    const tooltipRules = useMemo(() => {
      const maxLengthValue = 100;
      return {
        ...maxLengthWithSurrogate(
          maxLengthValue,
          t('transaction_items.validation_message.maxLength', {
            maxLength: maxLengthValue,
          })
        ),
      };
    }, [t]);

    const handleSubmit: SubmitHandler<TransactionItemDetailForm> = useCallback(
      (data) => {
        onSubmitUpdate({
          label: data.label,
          is_visible: data.visibility,
          tooltip_message: data.tooltip_message,
        });
      },
      [onSubmitUpdate]
    );

    const handleCancel = useCallback(() => {
      methods.reset();
      onView();
    }, [onView, methods]);

    return (
      <FormProvider {...methods}>
        <Form<TransactionItemDetailForm>
          formMethod={methods}
          onSubmit={handleSubmit}
        >
          <div className={cx(styles['container'])}>
            <div className={cx(styles['button-container'])}>
              <ButtonV2
                isSecondary
                onClick={handleCancel}
                isDisabled={isLoading}
              >
                {t('cancel')}
              </ButtonV2>
              <ButtonV2
                type='submit'
                isLoading={isLoading}
                isDisabled={isLoading}
              >
                {t('save')}
              </ButtonV2>
            </div>
            <ListView leftRightRatio={leftRightRatio}>
              <Top>
                <ListViewRequiredLabel fullWidth>
                  {t('transaction_items.table.label')}
                </ListViewRequiredLabel>
                <FormInput
                  name='label'
                  inputId='label'
                  control={methods.control}
                  rules={labelRules}
                />
              </Top>
              <Top>
                <ListViewLabel>
                  {t('transaction_items.table.is_visible')}
                </ListViewLabel>
                <FormCheckbox
                  name='visibility'
                  inputId='visibility'
                  control={methods.control}
                  label={t('transaction_items.table.visible')}
                />
              </Top>
              <Bottom>
                <ListViewLabel>
                  {t('transaction_items.table.tooltip_message')}
                </ListViewLabel>
                <FormInput
                  name='tooltip_message'
                  inputId='tooltip_message'
                  control={methods.control}
                  rules={tooltipRules}
                />
              </Bottom>
            </ListView>
          </div>
        </Form>
      </FormProvider>
    );
  }
);
EditForm.displayName = 'EditForm';
